/*
 * ProvabilityDrawer — the drill-in that renders a claim's RECEIPT.
 *
 * This is the physical embodiment of the core UX law (provability over
 * assertion). Pass a Claim with its Evidence; the drawer shows:
 *   - the verdict badge
 *   - every hash (mono, copyable, optionally linking to its audit entry)
 *   - the RFC 9421 signature base (the exact signed bytes)
 *   - the signing key's RFC 7638 thumbprint (NEVER the key material)
 *   - the spec section ref and source URL
 *
 * If a claim has NO evidence, the drawer says so plainly — it does not invent
 * a receipt. That honesty is the product.
 */

import { useEffect } from "react";
import type { Claim } from "../api/types";
import { Hash } from "./Hash";
import { VerifyBadge } from "./VerifyBadge";
import { SpecRef } from "./SpecRef";

interface ProvabilityDrawerProps {
  /** The claim to substantiate, or null when the drawer is closed. */
  claim: Claim | null;
  onClose: () => void;
}

export function ProvabilityDrawer({ claim, onClose }: ProvabilityDrawerProps) {
  useEffect(() => {
    if (!claim) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [claim, onClose]);

  if (!claim) return null;
  const ev = claim.evidence;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Proof — ${claim.label}`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      {/* scrim */}
      <button
        type="button"
        aria-label="Close proof"
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(5,7,14,0.6)",
          border: "none",
          cursor: "pointer",
        }}
      />
      <aside
        style={{
          position: "relative",
          width: "min(560px, 100vw)",
          height: "100%",
          background: "var(--bg-2)",
          borderLeft: "1px solid var(--hair-strong)",
          boxShadow: "var(--shadow-2)",
          overflowY: "auto",
          padding: "24px",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "16px",
            marginBottom: "8px",
          }}
        >
          <div>
            <div
              className="mono"
              style={{
                fontSize: "11px",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--fg-faint)",
              }}
            >
              Proof receipt
            </div>
            <h2 style={{ fontSize: "22px", marginTop: "6px" }}>{claim.label}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "1px solid var(--hair)",
              borderRadius: "6px",
              color: "var(--fg-muted)",
              width: "36px",
              height: "36px",
              flex: "0 0 auto",
            }}
          >
            ✕
          </button>
        </header>

        <div style={{ margin: "10px 0 20px" }}>
          <VerifyBadge state={claim.verdict} />
        </div>

        {!ev ? (
          <p style={{ color: "var(--fg-muted)" }}>
            No evidence was supplied for this claim. Per the provability law,
            the console will not fabricate a receipt — this verdict is shown as{" "}
            <strong>unsubstantiated</strong>.
          </p>
        ) : (
          <>
            {ev.hashes && ev.hashes.length > 0 && (
              <Section title="Hashes">
                <ul style={listStyle}>
                  {ev.hashes.map((h, i) => (
                    <li key={i} style={rowStyle}>
                      <span style={keyStyle}>{h.label}</span>
                      <Hash value={h.value} />
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {ev.keyThumbprint && (
              <Section title="Signing key — RFC 7638 thumbprint">
                <div className="mono-surface">{ev.keyThumbprint}</div>
                <p style={noteStyle}>
                  Thumbprint only. The console never renders key material.
                </p>
              </Section>
            )}

            {ev.algorithm && (
              <Section title="Algorithm">
                <div className="mono-surface">
                  <SpecRef text={ev.algorithm} />
                </div>
              </Section>
            )}

            {ev.signatureBase && (
              <Section title="RFC 9421 signature base">
                <pre
                  className="mono-surface"
                  style={{
                    whiteSpace: "pre-wrap",
                    margin: 0,
                    fontSize: "12px",
                    lineHeight: 1.55,
                  }}
                >
                  {ev.signatureBase}
                </pre>
                <p style={noteStyle}>
                  These are the exact bytes that were signed and verified.
                </p>
              </Section>
            )}

            {ev.detail && ev.detail.length > 0 && (
              <Section title="Detail">
                <ul style={listStyle}>
                  {ev.detail.map((d, i) => (
                    <li key={i} style={rowStyle}>
                      <span style={keyStyle}>{d.label}</span>
                      <span className="mono" style={{ fontSize: "12.5px" }}>
                        {d.value}
                      </span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {(ev.specRef || ev.source) && (
              <Section title="Provenance">
                {ev.specRef && (
                  <div style={rowStyle}>
                    <span style={keyStyle}>Spec</span>
                    <span className="mono" style={{ fontSize: "12.5px" }}>
                      <SpecRef text={ev.specRef} />
                    </span>
                  </div>
                )}
                {ev.source && (
                  <div style={rowStyle}>
                    <span style={keyStyle}>Source</span>
                    <span className="mono" style={{ fontSize: "12.5px", overflowWrap: "anywhere" }}>
                      {ev.source}
                    </span>
                  </div>
                )}
              </Section>
            )}
          </>
        )}
      </aside>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: "20px" }}>
      <h3
        className="mono"
        style={{
          fontSize: "11px",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--indigo)",
          marginBottom: "8px",
          fontFamily: "var(--font-mono)",
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

const listStyle: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};
const rowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "baseline",
  gap: "8px",
  justifyContent: "space-between",
};
const keyStyle: React.CSSProperties = {
  color: "var(--fg-faint)",
  fontSize: "12px",
  fontFamily: "var(--font-mono)",
};
const noteStyle: React.CSSProperties = {
  color: "var(--fg-faint)",
  fontSize: "12px",
  marginTop: "6px",
};
